/*
  "WIDGET PLUGIN"

  This flexible plugin is useful for creating multiple dropdown menus, popovers, and other dynamic components on the same page

  Quick summary:
  - This plugin makes it really easy to add and remove a "widget-activated" class to an element when another element is clicked

  Basic function:
  - You can create multiple "widgets" with this plugin, all on the same page
  - Each one can can be activated/deactivated using simple rules
  - Activated widgets have the class "widget-activated"
  - By default, clicking elsewhere on the page will deactivate currently active widgets

  Important classes/attributes:
  - ".js-widget"
	- Defines a "widget"
	  - This is the element the "widget-activated" class is added and removed from
	- Must be used with the "data-widget-id" attribute on the same element
	  - This gives the widget a name, so it can be referenced from the ".js-widget-trigger" and ".js-widget-prevent-trigger" elements
  - ".js-widget-trigger"
	- Defines a widget trigger (can be used inside or outside the ".js-widget" element)
	- Must be used with both of the following attributes on the same element
	  - "data-widget-id"
	  - "data-widget-trigger"
		- possible values: activate|deactivate|toggle
		  - "toggle" will toggle the "widget-activated" class
		  - "activate" will add (or keep) the "widget-activated" class
  - ".js-widget-prevent-trigger"
	- Defines an element that, when clicked, won't activate/deactivate a specific widget (can be used inside or outside the ".js-widget" element)
	- Must be used with the "data-widget-id" attribute on the same element
	  - Prevent a specific widget from being triggered by setting "data-widget-id" to the widget's id
	  - Prevent all widgets from being affected by setting "data-widget-id" to "all"
	- If a trigger element is inside this element, the trigger will happen anyways
	  - This is necessary in order to put a close button inside a widget
  - "data-widget-id"
	- Used in combination with the above classes to signal which widget you're referring to
  - ".widget-activated"
	- This class is dynamically applied most of the time
	- You can add it to the ".js-widget" element if you want it to start activated

  Example Slim Code:

  a.js-widget-trigger data-widget-trigger="toggle" data-widget-id="class-sidebar" Toggle Sidebar
  .js-widget.js-widget-prevent-trigger data-widget-id="class-sidebar"
	a.js-widget-trigger data-widget-trigger="deactivate" data-widget-id="class-sidebar" Close Sidebar

*/

import $ from 'jquery'

export type WidgetTriggerAction = 'activate' | 'deactivate' | 'toggle'

export interface WidgetEventTarget extends EventTarget {
	dispatchEvent(event: Event): boolean
}

export default function initWidgetPlugin(): void {
	$('.js-widget').on('widget-activated', function () {
		$(this).addClass('widget-activated')
	})

	$('.js-widget').on('widget-deactivated', function () {
		$(this).removeClass('widget-activated')
	})

	$(document).on('click', function (event: JQuery.ClickEvent) {
		const $target = $(event.target as Element)
		const $dontTrigger = $target.closest('.js-widget-prevent-trigger')
		const dontTriggerId = $dontTrigger.data('widget-id') as string | undefined
		const $widgetTrigger = $target.closest('.js-widget-trigger')
		const triggerId = $widgetTrigger.data('widget-id') as string | undefined
		const triggerAction = $widgetTrigger.data('widget-trigger') as WidgetTriggerAction | undefined
		const isTriggerElemInDontTriggerElem = $dontTrigger.find($widgetTrigger).length > 0

		if (dontTriggerId === 'all') {
			// special case
			return
		}

		let $currentWidget: JQuery<Element> | undefined

		if (isTriggerElemInDontTriggerElem || dontTriggerId !== triggerId) {
			$currentWidget = $(`.js-widget[data-widget-id='${triggerId}']`)

			if (triggerAction === 'activate') {
				$currentWidget.trigger('widget-activated')
				const element = $currentWidget.get(0) as WidgetEventTarget | undefined

				element?.dispatchEvent(new Event('widget-activated'))
			} else if (triggerAction === 'deactivate') {
				$currentWidget.trigger('widget-deactivated')
				const element = $currentWidget.get(0) as WidgetEventTarget | undefined

				element?.dispatchEvent(new Event('widget-deactivated'))
			} else if (triggerAction === 'toggle') {
				if ($currentWidget.hasClass('widget-activated')) {
					$currentWidget.trigger('widget-deactivated')
					const element = $currentWidget.get(0) as WidgetEventTarget | undefined

					element?.dispatchEvent(new Event('widget-deactivated'))
				} else {
					$currentWidget.trigger('widget-activated')
					const element = $currentWidget.get(0) as WidgetEventTarget | undefined

					element?.dispatchEvent(new Event('widget-activated'))
				}
			}
		}

		$('.widget-activated').each(function (index: number, widgetElem: Element) {
			const $widgetElem = $(widgetElem)
			const isCurrentWidget = $currentWidget ? widgetElem === $currentWidget.get(0) : false
			const hasPreventTrigger = $widgetElem.data('widget-id') === dontTriggerId

			if (!isCurrentWidget && !hasPreventTrigger) {
				$widgetElem.trigger('widget-deactivated')
			}
		})
	})
}
